(() => {
  if (customElements.get('dh-site-header')) return;

  class DhSiteHeader extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      // Track keyboard intent explicitly: touch-triggered programmatic focus
      // must not inherit the theme's legacy square focus shadow.
      this.dataset.keyboardFocus = 'false';
      document.addEventListener('keydown', (event) => {
        if (['Tab', 'Enter', ' ', 'Escape', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
          this.dataset.keyboardFocus = 'true';
        }
      }, { capture: true, signal: this.signal });
      const pointerFocus = () => { this.dataset.keyboardFocus = 'false'; };
      document.addEventListener('pointerdown', pointerFocus, { capture: true, signal: this.signal });
      document.addEventListener('touchstart', pointerFocus, { capture: true, passive: true, signal: this.signal });


      this.menuToggle = this.querySelector('[data-dh-menu-toggle]');
      this.menuClose = this.querySelector('[data-dh-menu-close]');
      this.drawer = this.querySelector('[data-dh-mobile-drawer]');
      this.backdrop = this.querySelector('[data-dh-menu-backdrop]');
      this.shopMenu = this.querySelector('[data-dh-shop-menu]');
      this.shopToggle = this.querySelector('[data-dh-shop-toggle]');
      this.shopPanel = this.querySelector('[data-dh-shop-panel]');
      this.search = this.querySelector('[data-dh-search]');
      this.cartLink = this.querySelector('#cart-icon-bubble');
      this.mobileCartLink = this.querySelector('[data-dh-mobile-cart]');
      this.announcement = this.querySelector('[data-dh-announcement]');
      this.announcementToggle = this.querySelector('[data-dh-announcement-toggle]');
      this.announcementItems = [...this.querySelectorAll('[data-dh-announcement-item]')];
      this.announcementIndex = Math.max(0, this.announcementItems.findIndex((item) => !item.hidden));
      this.announcementTimer = 0;
      this.announcementHovered = false;
      this.announcementFocused = false;
      this.announcementManuallyPaused = false;
      this.interval = Math.max(3000, Number(this.dataset.interval) || 4000);
      this.canRotate =
        this.dataset.autoRotate === 'true' &&
        this.announcementItems.length > 1 &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.bindMenu();
      this.bindCartCount();
      this.bindCartDrawer();
      this.bindShopMenu();
      this.bindSearch();
      this.bindAnnouncements();
      this.bindGlobalEvents();
      this.updateThemeColor();
      this.headerSizeObserver = new ResizeObserver(() => {
        document.documentElement.style.setProperty('--dh-site-header-offset', `${this.getBoundingClientRect().height}px`);
      });
      this.headerSizeObserver.observe(this);
      this.scheduleAnnouncement();
    }

    disconnectedCallback() {
      this.menuAnimation?.cancel();
      this.backdropAnimation?.cancel();
      document.body.classList.remove('dh-site-menu-open');
      this.controller?.abort();
      this.cartObserver?.disconnect();
      this.headerSizeObserver?.disconnect();
      window.clearTimeout(this.announcementTimer);
      this.cartDrawerBoundLink = null;
      document.body.classList.remove('dh-site-menu-open');
      delete this.dataset.ready;
    }

    bindMenu() {
      if (!this.menuToggle || !this.drawer || !this.backdrop) return;

      this.menuToggle.addEventListener(
        'click',
        () => (this.menuToggle.getAttribute('aria-expanded') === 'true' ? this.closeMenu() : this.openMenu()),
        { signal: this.signal },
      );
      this.backdrop.addEventListener('click', () => this.closeMenu(), { signal: this.signal });
      this.menuClose?.addEventListener('click', () => this.closeMenu(), { signal: this.signal });
      this.drawer.addEventListener(
        'click',
        (event) => {
          const link = event.target.closest('a');
          if (!link) return;
          this.closeMenu(false);
          if (link === this.mobileCartLink) {
            const cartDrawer = document.querySelector('cart-drawer');
            if (!cartDrawer || typeof cartDrawer.open !== 'function') return;
            event.preventDefault();
            // The menu cart link is now hidden. Return keyboard focus to the
            // visible menu trigger when the cart closes instead.
            cartDrawer.open(this.menuToggle);
          }
        },
        { signal: this.signal },
      );
    }

    animateMenu(open, onFinish) {
      const previousTransform = this.drawer.hidden ? 'translateX(-100%)' : getComputedStyle(this.drawer).transform;
      const previousOpacity = this.backdrop.hidden ? '0' : getComputedStyle(this.backdrop).opacity;
      this.menuAnimation?.cancel();
      this.backdropAnimation?.cancel();
      this.drawer.hidden = false;
      this.backdrop.hidden = false;
      const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches || window.innerWidth >= 990 ? 0 : 560;
      const options = { duration, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' };
      const animation = this.drawer.animate([
        { transform: previousTransform },
        { transform: open ? 'translateX(0)' : 'translateX(-100%)' },
      ], options);
      this.menuAnimation = animation;
      this.backdropAnimation = this.backdrop.animate([
        { opacity: previousOpacity }, { opacity: open ? 1 : 0 },
      ], options);
      animation.onfinish = () => {
        if (this.menuAnimation !== animation) return;
        onFinish?.();
        animation.cancel();
        this.backdropAnimation?.cancel();
        this.menuAnimation = null;
      };
    }

    openMenu() {
      if (!this.menuToggle || !this.drawer || !this.backdrop) return;
      this.closeShopMenu();
      if (this.search?.open) this.search.removeAttribute('open');
      this.drawer.inert = false;
      this.menuToggle.setAttribute('aria-expanded', 'true');
      this.menuToggle.setAttribute('aria-label', this.menuToggle.dataset.closeLabel || 'Close menu');
      document.body.classList.add('dh-site-menu-open');
      this.updateThemeColor(true);
      this.animateMenu(true);
      requestAnimationFrame(() => {
        if (this.menuToggle.getAttribute('aria-expanded') === 'true') {
          this.drawer.querySelector('summary, a, button')?.focus({ preventScroll: true });
        }
      });
    }

    closeMenu(returnFocus = true) {
      if (!this.menuToggle || !this.drawer || !this.backdrop) return;
      const wasOpen = this.menuToggle.getAttribute('aria-expanded') === 'true';
      if (!wasOpen) return;
      this.menuToggle.setAttribute('aria-expanded', 'false');
      this.menuToggle.setAttribute('aria-label', this.menuToggle.dataset.openLabel || 'Open menu');
      if (returnFocus) this.menuToggle.focus({ preventScroll: true });
      this.drawer.inert = true;
      this.animateMenu(false, () => {
        this.drawer.hidden = true;
        this.backdrop.hidden = true;
        this.drawer.inert = false;
        document.body.classList.remove('dh-site-menu-open');
        this.updateThemeColor(false);
      });
    }

    updateThemeColor(menuOpen = this.menuToggle?.getAttribute('aria-expanded') === 'true') {
      const styles = getComputedStyle(this);
      const colorProperty = menuOpen
        ? '--dh-site-drawer-bg'
        : this.announcement ? '--dh-site-announcement-bg' : '--dh-site-header-bg';
      const color = styles.getPropertyValue(colorProperty).trim() || (menuOpen ? '#f1f9ff' : '#eeee4b');
      document.querySelector('meta[name="theme-color"][data-dh-theme-color]')?.setAttribute('content', color);
      document.documentElement.style.setProperty('--dh-site-safe-area-bg', color);
    }

    bindCartCount() {
      if (!this.cartLink) return;
      const synchronize = () => {
        const source = this.cartLink.querySelector('[data-dh-cart-count]');
        if (!source) return;
        const count = Math.max(0, Number(source.dataset.dhCartCount) || 0);
        const countText = count > 99 ? '99+' : String(count);
        const textLabel = this.cartLink.dataset.dhCartLabel;
        const primaryLabel = this.cartLink.querySelector('.dh-site-header__action-text');
        if (textLabel && primaryLabel && primaryLabel.textContent !== textLabel) primaryLabel.textContent = textLabel;
        const accessibleLabel = this.cartLink.querySelector('[data-dh-cart-count-label]')?.textContent.trim();
        const baseLabel = textLabel?.trim() || primaryLabel?.textContent.trim() || 'Cart';
        const cartAccessibleLabel = count > 0 && accessibleLabel ? `${baseLabel}, ${accessibleLabel}` : baseLabel;

        [this.cartLink, this.mobileCartLink].filter(Boolean).forEach((link) => {
          const indicator = link.querySelector('[data-dh-cart-count]');
          if (!indicator) return;
          if (indicator.dataset.dhCartCount !== String(count)) indicator.dataset.dhCartCount = String(count);
          if (indicator.textContent !== countText) indicator.textContent = countText;
          indicator.dataset.dhCartCountLength = String(countText.length);
          indicator.hidden = count === 0;
          const label = link.querySelector('[data-dh-cart-count-label]');
          if (label && accessibleLabel && label.textContent !== accessibleLabel) label.textContent = accessibleLabel;
          if (link.getAttribute('aria-label') !== cartAccessibleLabel) link.setAttribute('aria-label', cartAccessibleLabel);
        });
      };

      // Shopify's normal cart section responses replace the primary icon's
      // contents. Observe that authoritative count instead of guessing from
      // add-to-cart clicks, so removals and quantity edits stay in sync too.
      this.cartObserver = new MutationObserver(synchronize);
      this.cartObserver.observe(this.cartLink, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['data-dh-cart-count'],
      });
      synchronize();
    }

    bindCartDrawer() {
      const link = this.cartLink;
      const drawer = document.querySelector('cart-drawer');
      if (!link || !drawer) return;
      const signal = this.signal;
      const bind = () => {
        if (signal.aborted || !this.isConnected || this.cartLink !== link) return;
        if (document.querySelector('#cart-icon-bubble') !== link || typeof drawer.open !== 'function') return;
        if (this.cartDrawerBoundLink === link) return;

        // Dawn binds the initial anchor when its drawer upgrades. Keep that
        // listener; only an editor-replaced header needs a new binding. A
        // header-owned anchor can reconnect after its old signal was aborted.
        const dawnOwnsLink = link.getAttribute('role') === 'button'
          && link.getAttribute('aria-haspopup') === 'dialog'
          && link.dataset.dhCartDrawerOwner !== 'header';
        if (dawnOwnsLink) return;

        link.setAttribute('role', 'button');
        link.setAttribute('aria-haspopup', 'dialog');
        link.dataset.dhCartDrawerOwner = 'header';
        this.cartDrawerBoundLink = link;
        link.addEventListener('click', (event) => {
          event.preventDefault();
          drawer.open(link);
        }, { signal });
        link.addEventListener('keydown', (event) => {
          if (event.code?.toUpperCase() !== 'SPACE' && event.key !== ' ') return;
          event.preventDefault();
          drawer.open(link);
        }, { signal });
      };

      if (customElements.get('cart-drawer')) bind();
      else customElements.whenDefined('cart-drawer').then(bind);
    }

    bindShopMenu() {
      if (!this.shopMenu || !this.shopToggle || !this.shopPanel) return;

      this.shopToggle.addEventListener(
        'click',
        (event) => {
          const isOpen = this.shopToggle.getAttribute('aria-expanded') === 'true';

          // A pointer enters the trigger before its click fires, so the hover
          // handler may already have opened the panel. Keep that pointer-opened
          // panel open; keyboard activation still acts as a conventional toggle.
          if (event.detail > 0 && isOpen) return;
          if (isOpen) this.closeShopMenu();
          else this.openShopMenu();
        },
        { signal: this.signal },
      );
      this.shopMenu.addEventListener('mouseenter', () => this.openShopMenu(), { signal: this.signal });
      this.shopMenu.addEventListener('mouseleave', () => this.closeShopMenu(), { signal: this.signal });
      this.shopMenu.addEventListener(
        'focusout',
        (event) => {
          if (!this.shopMenu.contains(event.relatedTarget)) this.closeShopMenu();
        },
        { signal: this.signal },
      );
    }

    openShopMenu() {
      if (!this.shopMenu || !this.shopToggle || !this.shopPanel || window.innerWidth < 990) return;
      this.shopPanel.hidden = false;
      this.shopMenu.classList.add('is-open');
      this.shopToggle.setAttribute('aria-expanded', 'true');
    }

    closeShopMenu() {
      if (!this.shopMenu || !this.shopToggle || !this.shopPanel) return;
      this.shopPanel.hidden = true;
      this.shopMenu.classList.remove('is-open');
      this.shopToggle.setAttribute('aria-expanded', 'false');
    }

    bindSearch() {
      const close = this.querySelector('[data-dh-search-close]');
      if (!this.search || !close) return;
      close.addEventListener('click', () => this.closeSearch(), { signal: this.signal });
    }

    closeSearch(returnFocus = true) {
      if (!this.search?.open) return;
      this.search.removeAttribute('open');
      if (returnFocus) this.search.querySelector('summary')?.focus();
    }

    bindAnnouncements() {
      if (!this.announcement) return;

      this.announcementToggle?.addEventListener(
        'click',
        () => {
          this.announcementManuallyPaused = !this.announcementManuallyPaused;
          this.announcementToggle.setAttribute('aria-pressed', String(this.announcementManuallyPaused));
          this.announcementToggle.setAttribute(
            'aria-label',
            this.announcementManuallyPaused
              ? this.announcementToggle.dataset.playLabel || 'Play announcements'
              : this.announcementToggle.dataset.pauseLabel || 'Pause announcements',
          );
          this.announcementToggle.querySelector('[aria-hidden="true"]').textContent = this.announcementManuallyPaused ? '▶' : 'Ⅱ';
          if (this.announcementManuallyPaused) window.clearTimeout(this.announcementTimer);
          else this.scheduleAnnouncement();
        },
        { signal: this.signal },
      );

      this.announcement.addEventListener(
        'pointerenter',
        (event) => {
          if (event.pointerType === 'touch') return;
          this.announcementHovered = true;
          window.clearTimeout(this.announcementTimer);
        },
        { signal: this.signal },
      );
      this.announcement.addEventListener(
        'pointerleave',
        (event) => {
          if (event.pointerType === 'touch') return;
          this.announcementHovered = false;
          this.scheduleAnnouncement();
        },
        { signal: this.signal },
      );
      this.announcement.addEventListener(
        'focusin',
        () => {
          this.announcementFocused = true;
          window.clearTimeout(this.announcementTimer);
        },
        { signal: this.signal },
      );
      this.announcement.addEventListener(
        'focusout',
        (event) => {
          if (this.announcement.contains(event.relatedTarget)) return;
          this.announcementFocused = false;
          this.scheduleAnnouncement();
        },
        { signal: this.signal },
      );
    }

    get announcementPaused() {
      return Boolean(this.announcementHovered || this.announcementFocused);
    }

    showAnnouncement(index) {
      if (!this.announcementItems.length) return;
      this.announcementIndex = ((index % this.announcementItems.length) + this.announcementItems.length) % this.announcementItems.length;
      this.announcementItems.forEach((item, itemIndex) => {
        const active = itemIndex === this.announcementIndex;
        item.hidden = !active;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-hidden', String(!active));
      });
    }

    scheduleAnnouncement() {
      window.clearTimeout(this.announcementTimer);
      if (!this.isConnected || !this.canRotate || this.announcementPaused || this.announcementManuallyPaused || document.hidden) return;
      this.announcementTimer = window.setTimeout(() => {
        this.showAnnouncement(this.announcementIndex + 1);
        this.scheduleAnnouncement();
      }, this.interval);
    }

    bindGlobalEvents() {
      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Tab' && this.menuToggle?.getAttribute('aria-expanded') === 'true') {
            const drawerControls = [
              ...this.drawer.querySelectorAll('a[href], summary, button:not([disabled]), [tabindex]:not([tabindex="-1"])'),
            ].filter((element) => !element.hidden && element.getClientRects().length > 0);
            const firstControl = drawerControls[0];
            const lastControl = drawerControls[drawerControls.length - 1];

            if (event.shiftKey && document.activeElement === firstControl) {
              event.preventDefault();
              lastControl?.focus();
            } else if (!event.shiftKey && document.activeElement === lastControl) {
              event.preventDefault();
              firstControl?.focus();
            }
            return;
          }

          if (event.key !== 'Escape') return;
          if (this.menuToggle?.getAttribute('aria-expanded') === 'true') this.closeMenu();
          this.closeShopMenu();
          if (this.search?.open) this.closeSearch();
        },
        { signal: this.signal },
      );

      document.addEventListener(
        'pointerdown',
        (event) => {
          if (this.shopMenu && !this.shopMenu.contains(event.target)) this.closeShopMenu();
          if (this.search?.open && !this.search.contains(event.target)) this.closeSearch(false);
        },
        { signal: this.signal },
      );

      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.hidden) window.clearTimeout(this.announcementTimer);
          else this.scheduleAnnouncement();
        },
        { signal: this.signal },
      );

      window.addEventListener(
        'resize',
        () => {
          if (window.innerWidth >= 990) this.closeMenu(false);
          else this.closeShopMenu();
        },
        { signal: this.signal, passive: true },
      );

      document.addEventListener(
        'shopify:block:select',
        (event) => {
          const item = event.target.closest?.('[data-dh-announcement-item]');
          if (!item || !this.contains(item)) return;
          const index = this.announcementItems.indexOf(item);
          if (index >= 0) this.showAnnouncement(index);
        },
        { signal: this.signal },
      );
    }
  }

  customElements.define('dh-site-header', DhSiteHeader);
})();
